import { Resources } from 'constants/resources';
import { PAYMENT_ROLES, Role } from 'constants/roles';

import React, { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { createMerchantAccesses } from 'config/requests/merchants';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify } from 'react-admin';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { CancelButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { AutocompleteMultiselectInput } from 'shared/react-admin/Inputs';
import { ReferenceArrayInput } from 'shared/react-admin/ReferenceInputs';
import { User } from 'types/embedded/user';
import { getId } from 'utils';

import { useYupSchema } from './hooks/useYupSchema';
import { BoxStyled, DialogStyled, FormBoxStyled } from './MerchantShowAccessCreateDialog.styled';

const ROLES_FILTER = { alias: [...PAYMENT_ROLES, Role.Risk, Role.Observer] };

type Props = {
  open: boolean;
  onClose: () => void;
  merchantId: string | undefined;
};

type FormValues = {
  users: string[];
  roles: string[];
};

export const MerchantShowAccessCreateDialog: React.FC<Props> = ({
  open,
  onClose: onCloseProp,
  merchantId,
}) => {
  const translate = useTypedTranslate();
  const notify = useNotify();
  const schema = useYupSchema();
  const form = useForm<FormValues>({
    defaultValues: { roles: [], users: [] },
    resolver: yupResolver(schema),
  });
  const {
    formState: { isValid, isSubmitting },
  } = form;

  const rolesWatched = form.watch('roles');

  useEffect(() => {
    form.resetField('users');
  }, [form, rolesWatched]);

  const { mutate } = useMutation({
    mutationFn: ({ merchant, users }: { merchant: number; users: number[] }) =>
      createMerchantAccesses(merchant, users),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [`${Resources.Merchants}/${getId(merchantId)}/merchant_accesses`],
      });
      onClose();
      notify('Доступ добавлен', { type: 'success' });
    },
    onError: (error: any) => {
      notify(error.data?.errors[0].title || translate('app.common.error'), {
        type: 'error',
      });
    },
  });

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    mutate({
      merchant: Number(getId(merchantId)),
      users: values.users.map((user) => Number(getId(user))),
    });
  };

  const onClose = () => {
    form.reset();
    onCloseProp();
  };

  return (
    <DialogStyled onClose={onClose} open={open} width="640px">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <BoxStyled>
            <CardHeader
              appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
              title="Добавить доступ"
              titleSize="smallBold"
            />
            <CardContent>
              <FormBoxStyled>
                <Alert severity="warning">
                  После применения настроек доступа, выбранным пользователям будет предоставлен
                  доступ к настраиваемому мерчанту в соответствии с ролями в системе.
                </Alert>
                <ReferenceArrayInput
                  filter={ROLES_FILTER}
                  perPage={100}
                  reference="roles"
                  source="roleEntities"
                >
                  <AutocompleteMultiselectInput
                    helperText={false}
                    isRequired
                    label="Роли"
                    name="roles"
                    optionText="name"
                    size="small"
                  />
                </ReferenceArrayInput>
                <ReferenceArrayInput
                  filter={{ roleEntities: rolesWatched }}
                  perPage={200}
                  reference="users"
                  source="users"
                >
                  <AutocompleteMultiselectInput
                    disabled={!rolesWatched?.length}
                    getOptionLabel={(record: User) => `${record.firstName} ${record.lastName}`}
                    helperText={false}
                    isRequired
                    label="Пользователи"
                    name="users"
                    size="small"
                  />
                </ReferenceArrayInput>
              </FormBoxStyled>
            </CardContent>
            <CardActionsButtons
              rightActionsSlot={[
                <CancelButton key="cancel-button" onClick={onClose} variant="outlined" />,
                <SaveButton
                  disabled={!isValid || isSubmitting}
                  key="save-button"
                  label="Сохранить"
                  variant="contained"
                />,
              ]}
            />
          </BoxStyled>
        </form>
      </FormProvider>
    </DialogStyled>
  );
};
